import React, { useState, useEffect } from 'react';
import { Menu, X, Wine, Phone, MapPin } from 'lucide-react';
import { businessData } from '../data/mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    return () => document.body.classList.remove('menu-open');
  }, [isMenuOpen]);

  const navLinks = [
    { href: '#inicio', label: 'Inicio' },
    { href: '#productos', label: 'Productos' },
    { href: '#opiniones', label: 'Opiniones' },
    { href: '#contacto', label: 'Contacto' }
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-[var(--color-wine)] shadow-lg py-2'
            : 'bg-transparent py-4'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          <a href="#inicio" className="flex items-center gap-2">
            <img
              src={businessData.logo}
              alt={`${businessData.name} - ${businessData.tagline}`}
              className="h-12 md:h-14 w-auto rounded"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`font-medium transition-colors hover:text-[var(--color-gold)] ${
                  isScrolled ? 'text-[var(--color-cream)]' : 'text-[var(--color-cream)]'
                }`}
              >
                {link.label}
              </a>
            ))}
            <a
              href={businessData.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[var(--color-cream)] text-[var(--color-wine)] px-5 py-2 rounded font-semibold transition-all hover:bg-[var(--color-gold)] hover:text-[var(--color-black)] hover:scale-105"
            >
              WhatsApp
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-[var(--color-cream)] hover:text-[var(--color-gold)] transition-colors z-50"
            aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-40 transition-opacity duration-300 md:hidden ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMenuOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[280px] bg-[var(--color-wine)] z-50 transform transition-transform duration-300 ease-out md:hidden ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full pt-20 px-6">
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-[var(--color-cream)] text-lg font-medium py-3 border-b border-[var(--color-wine-light)] hover:text-[var(--color-gold)] transition-colors"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="mt-8 space-y-4">
            <a
              href={businessData.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[var(--color-gold)] text-[var(--color-black)] px-5 py-3 rounded font-medium w-full transition-all hover:bg-[var(--color-gold-light)]"
            >
              <Phone size={18} />
              WhatsApp
            </a>
            <a
              href={businessData.googleMapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 border-2 border-[var(--color-cream)] text-[var(--color-cream)] px-5 py-3 rounded font-medium w-full transition-all hover:bg-[var(--color-cream)] hover:text-[var(--color-wine)]"
            >
              <MapPin size={18} />
              Cómo llegar
            </a>
          </div>

          <div className="mt-auto pb-8">
            <div className="flex items-center gap-2 text-[var(--color-cream)]/70 text-sm">
              <Wine size={16} />
              <span>{businessData.tagline}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
